import {
  getVideoModelConfig,
  getVideoReferenceAudioError,
} from "~/common/video/videoModels"
import { useEffect, useMemo, useRef, useState } from "react"
import { message } from "antd"
import { useNavigate } from "react-router"
import {
  HotVideoAudioType,
  getSafeVideoDuration,
  getSafeVideoRatio,
  getSafeVideoResolution,
  hasLimitedVideoTextFidelity,
} from "~/common/constants"
import {
  clearHomePromptTransfer,
  readHomePromptTransfer,
} from "~/common/tasks/homePromptTransfer"
import { getVideoGenerationPointCost } from "~/common/billing/pointCosts"
import { reconcileImageMentionLabels } from "~/common/media/resourceMentions"
import {
  createLocalUploadResources,
  fetchRemoteResourceFile,
  getRemoteResourceFetchErrorMessage,
  getUploadableLocalFiles,
  uploadLocalResources,
} from "~/common/media/resourceUpload"
import {
  RESOURCE_UPLOAD_PASTE_PRIORITY,
  useResourceUploadInteractions,
} from "~/common/media/resourceUploadHooks"
import { getTaskListPath } from "~/common/router"
import {
  getUploadedResourceKind,
  getUploadedResourceMaterialLabel,
  type UploadedResource,
} from "~/common/media/uploadedResources"
import { type VideoParameterValue, usePointBalanceGuard } from "~/components"
import type { AssetPromptInputRef } from "~/components/AssetPromptInput/types"
import { useDefaultVideoOptions, useSessionStore } from "~/store/session"
import {
  focusHomePromptInput,
  getReferenceSummary,
  mergeResources,
  validateReferences,
} from "./helpers"
import { VideoGenerationRequestValidationError } from "~/common/video/videoGenerationRequest"
import { createDirectVideo } from "~/api/task"
import {
  notifyUploadFailures,
  notifyUploadSuccess,
} from "~/common/media/uploadFeedback"

const getImageUrls = (resources: UploadedResource[]) => {
  return resources
    .filter((resource) => getUploadedResourceKind(resource) === "image")
    .map((resource) => resource.url)
}

// 首页创作区的状态与流程：提示词、参考素材上传 / 抓取、生成参数和提交
export const useHomeComposer = () => {
  const navigate = useNavigate()

  const openLoginModal = useSessionStore((state) => state.openLoginModal)
  const user = useSessionStore((state) => state.user)
  const { videoOptions: defaultVideoOptions } = useDefaultVideoOptions()
  const { runPointAction } = usePointBalanceGuard()
  const [prompt, setPrompt] = useState("")
  const [isPromptExpanded, setIsPromptExpanded] = useState(false)
  const promptInputRef = useRef<AssetPromptInputRef | null>(null)
  const composerRef = useRef<HTMLElement | null>(null)
  const shouldScrollToComposerRef = useRef(false)
  const [videoOptions, setVideoOptions] =
    useState<VideoParameterValue>(defaultVideoOptions)
  const [references, setReferences] = useState<UploadedResource[]>([])
  const [mentionedResources, setMentionedResources] = useState<
    UploadedResource[]
  >([])
  const [isSubmittingVideoTask, setIsSubmittingVideoTask] = useState(false)
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false)
  const [uploadingReferenceCount, setUploadingReferenceCount] = useState(0)
  const [remoteFetchingReferenceCount, setRemoteFetchingReferenceCount] =
    useState(0)
  const { duration, model: videoModel, ratio, resolution } = videoOptions
  const shouldShowTextFidelityWarning = hasLimitedVideoTextFidelity(videoModel)
  const { maxImageCount, maxAudioCount } = getVideoModelConfig(videoModel)
  const maxReferenceCount = maxImageCount + maxAudioCount
  const allReferences = mergeResources(references, mentionedResources)
  const referenceSummary = getReferenceSummary(allReferences)
  const validationMessage = validateReferences(
    prompt,
    allReferences,
    videoModel,
    resolution,
  )
  const canGenerate = !validationMessage
  const materialType = getUploadedResourceMaterialLabel(allReferences)
  const videoPointCost = getVideoGenerationPointCost({
    duration: duration || 0,
    model: videoModel,
    referenceImageCount: getImageUrls(allReferences).length,
    resolution,
  })

  const expandPrompt = () => {
    setIsPromptExpanded(true)
  }

  const togglePrompt = () => {
    setIsPromptExpanded((expanded) => {
      if (!expanded) {
        shouldScrollToComposerRef.current = true
      }

      return !expanded
    })
  }

  useEffect(() => {
    setVideoOptions((currentValue) => ({
      ...currentValue,
      model: defaultVideoOptions.model,
      duration:
        typeof currentValue.duration === "number"
          ? getSafeVideoDuration(
              defaultVideoOptions.model,
              currentValue.duration,
            )
          : currentValue.duration,
      ratio: getSafeVideoRatio(defaultVideoOptions.model, currentValue.ratio),
      resolution: defaultVideoOptions.resolution,
    }))
  }, [defaultVideoOptions])

  useEffect(() => {
    const transferredPayload = readHomePromptTransfer()

    if (!transferredPayload?.prompt) return

    setReferences(transferredPayload.references || [])
    setPrompt(transferredPayload.prompt)
    setMentionedResources([])
    shouldScrollToComposerRef.current = true
    setIsPromptExpanded(true)

    setVideoOptions((currentValue) => {
      const nextVideoModel = transferredPayload.model ?? currentValue.model

      return {
        ...currentValue,
        duration:
          typeof transferredPayload.duration === "number"
            ? getSafeVideoDuration(nextVideoModel, transferredPayload.duration)
            : typeof currentValue.duration === "number"
              ? getSafeVideoDuration(nextVideoModel, currentValue.duration)
              : currentValue.duration,
        model: nextVideoModel,
        ratio: getSafeVideoRatio(
          nextVideoModel,
          transferredPayload.ratio ?? currentValue.ratio,
        ),
        resolution: getSafeVideoResolution(
          nextVideoModel,
          transferredPayload.resolution ?? currentValue.resolution,
        ),
      }
    })

    const clearTimer = window.setTimeout(() => {
      clearHomePromptTransfer()
    }, 1000)

    return () => {
      window.clearTimeout(clearTimer)
    }
  }, [])

  useEffect(() => {
    if (!isPromptExpanded || !shouldScrollToComposerRef.current) return

    let scrollFrame = 0
    const layoutFrame = window.requestAnimationFrame(() => {
      scrollFrame = window.requestAnimationFrame(() => {
        const composer = composerRef.current

        if (!composer) return

        shouldScrollToComposerRef.current = false
        focusHomePromptInput()
        composer.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      })
    })

    return () => {
      window.cancelAnimationFrame(layoutFrame)
      window.cancelAnimationFrame(scrollFrame)
    }
  }, [isPromptExpanded])

  const referenceUrlPayload = useMemo(() => {
    return allReferences.reduce(
      (payload, resource) => {
        const resourceKind = getUploadedResourceKind(resource)

        switch (resourceKind) {
          case "image":
            payload.imageUrls.push(resource.url)
            break
          case "audio":
            payload.audioUrls.push(resource.url)
            break
        }
        return payload
      },
      {
        audioUrls: [] as string[],
        imageUrls: [] as string[],
      },
    )
  }, [allReferences])

  const removeReference = (targetUrl: string) => {
    setReferences((currentValue) => {
      return currentValue.filter((resource) => resource.url !== targetUrl)
    })
  }

  const reorderReferences = (nextReferences: UploadedResource[]) => {
    const previousImageUrls = getImageUrls(references)
    const nextImageUrls = getImageUrls(nextReferences)

    setReferences(nextReferences)
    setPrompt(
      (currentValue) =>
        reconcileImageMentionLabels(
          currentValue,
          previousImageUrls,
          nextImageUrls,
        ).value,
    )
  }

  const uploadReferenceFiles = async (files: File[]) => {
    if (!files.length) return

    expandPrompt()

    if (!user) {
      openLoginModal()
      return
    }

    const { acceptedFiles, rejectedFiles, validationErrors } =
      getUploadableLocalFiles({
        allowedKinds: ["image", "audio"],
        files,
      })

    if (rejectedFiles.length) {
      void message.warning("首页输入框仅支持拖拽上传图片或音频")
    }

    if (validationErrors.length) {
      void message.error(validationErrors.join("；"))
    }

    if (!acceptedFiles.length) return

    const supportedFiles = acceptedFiles.map(({ file }) => file)
    const localResources = createLocalUploadResources(supportedFiles)
    const preflightError = validateReferences(
      prompt || "placeholder",
      mergeResources(allReferences, localResources),
      videoModel,
      resolution,
    )

    if (preflightError) {
      void message.warning(preflightError)
      return
    }

    setUploadingReferenceCount(acceptedFiles.length)

    try {
      const urls = acceptedFiles
        .filter(({ kind }) => kind === "audio")
        .map(({ file }) => URL.createObjectURL(file))
      try {
        const audioError = await getVideoReferenceAudioError(videoModel, [
          ...allReferences
            .filter((resource) => getUploadedResourceKind(resource) === "audio")
            .map(({ url }) => url),
          ...urls,
        ])
        if (audioError) {
          void message.warning(audioError)
          return
        }
      } finally {
        urls.forEach((url) => URL.revokeObjectURL(url))
      }
      const { failures, resources: nextResources } = await uploadLocalResources(
        {
          files: acceptedFiles,
        },
      )

      if (nextResources.length) {
        const nextReferences = mergeResources(references, nextResources)
        const validationError = validateReferences(
          prompt || "placeholder",
          mergeResources(nextReferences, mentionedResources),
          videoModel,
          resolution,
        )

        if (validationError) {
          void message.warning(validationError)
        } else {
          setReferences(nextReferences)
          notifyUploadSuccess(nextResources.length)
        }
      }

      notifyUploadFailures(failures, "上传素材失败")
    } finally {
      setUploadingReferenceCount(0)
    }
  }

  const uploadRemoteReferenceImage = async ({
    fallbackName,
    url,
  }: {
    fallbackName: string
    url: string
  }) => {
    if (!url) return false

    expandPrompt()

    if (!user) {
      openLoginModal()
      return false
    }

    setRemoteFetchingReferenceCount((currentValue) => currentValue + 1)

    try {
      const file = await fetchRemoteResourceFile({
        fallbackName,
        kind: "image",
        url,
      })

      await uploadReferenceFiles([file])
      return true
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : getRemoteResourceFetchErrorMessage("image")
      void message.error(errorMessage)
      return false
    } finally {
      setRemoteFetchingReferenceCount((currentValue) =>
        Math.max(0, currentValue - 1),
      )
    }
  }

  const isReferenceUploadBusy =
    uploadingReferenceCount > 0 || remoteFetchingReferenceCount > 0
  const { isDragging, surfaceProps } = useResourceUploadInteractions({
    allowRemoteImage: true,
    disabled: isReferenceUploadBusy,
    onFiles: (files) => {
      expandPrompt()
      void uploadReferenceFiles(files)
    },
    onRemoteImageUrl: (url) => {
      expandPrompt()
      void uploadRemoteReferenceImage({
        fallbackName: "远程图片",
        url,
      })
    },
    pasteActivation: "focus",
    pastePriority: RESOURCE_UPLOAD_PASTE_PRIORITY.HOME,
  })

  const handleAssetConfirm = (selectedResources: UploadedResource[]) => {
    expandPrompt()

    const validationError = validateReferences(
      prompt || "placeholder",
      mergeResources(selectedResources, mentionedResources),
      videoModel,
      resolution,
    )

    if (validationError) {
      void message.warning(validationError)
      return
    }

    setReferences(selectedResources)
    setIsAssetModalOpen(false)
  }

  const openAssetModal = () => {
    if (!user) {
      openLoginModal()
      return
    }

    setIsAssetModalOpen(true)
  }

  const generateVideo = async () => {
    if (isSubmittingVideoTask) return

    if (!user) {
      openLoginModal()
      return
    }

    if (validationMessage) {
      void message.warning(validationMessage)
      return
    }

    if (
      referenceUrlPayload.audioUrls.length > 0 &&
      referenceUrlPayload.imageUrls.length === 0
    ) {
      void message.warning(
        "当前仅上传了音频参考，无法生成视频，请补充上传图片素材后再提交",
      )
      return
    }

    const createVideoParams: API.Task.DirectCreateVideoParams = {
      audio_type: HotVideoAudioType.ai,
      audio_urls: referenceUrlPayload.audioUrls.length
        ? referenceUrlPayload.audioUrls
        : undefined,
      channel: videoModel,
      duration: duration || 0,
      image_urls: referenceUrlPayload.imageUrls,
      prompt: prompt.trim(),
      ratio,
      resolution,
    }
    try {
      setIsSubmittingVideoTask(true)

      const submitted = await runPointAction(
        videoPointCost,
        async () => {
          await createDirectVideo(createVideoParams, allReferences)
          return true
        },
        {
          videoOptions: {
            duration,
            model: videoModel,
            referenceImageCount: referenceUrlPayload.imageUrls.length,
            resolution,
          },
          refresh: "user",
        },
      )
      if (!submitted) return
      clearHomePromptTransfer()
      setPrompt("")
      setReferences([])
      setMentionedResources([])
      void message.success("AI 生视频任务已提交")
      navigate(getTaskListPath("origin"))
    } catch (error) {
      if (error instanceof VideoGenerationRequestValidationError) {
        void message.warning(error.message)
      }
    } finally {
      setIsSubmittingVideoTask(false)
    }
  }

  return {
    allReferences,
    canGenerate,
    composerRef,
    expandPrompt,
    generateVideo,
    handleAssetConfirm,
    isAssetModalOpen,
    isDragging,
    isPromptExpanded,
    isSubmittingVideoTask,
    materialType,
    maxAudioCount,
    maxImageCount,
    maxReferenceCount,
    openAssetModal,
    prompt,
    promptInputRef,
    referenceSummary,
    referenceUrlPayload,
    references,
    remoteFetchingReferenceCount,
    removeReference,
    reorderReferences,
    setIsAssetModalOpen,
    setMentionedResources,
    setPrompt,
    setVideoOptions,
    shouldShowTextFidelityWarning,
    surfaceProps,
    togglePrompt,
    uploadingReferenceCount,
    videoModel,
    videoOptions,
    videoPointCost,
  }
}
